import { Button } from '@/components/ui/button'

export interface PreviewInspectInteractiveElement {
  role?: string
  name?: string
  text?: string
  selector?: string
  tagName?: string
  placeholder?: string
  disabled?: boolean
  active?: boolean
  value?: string
}

export interface PreviewInspectDialogPresence extends PreviewInspectInteractiveElement {
  title?: string
  ariaModal?: boolean
  open?: boolean
}

export interface PreviewInspectObstructionElement {
  role?: string
  tagName?: string
  text?: string
  selector?: string
  reason: string
  zIndex?: number
}

export interface PreviewInspectObstructionDiagnostics {
  hasModal: boolean
  dialogCount: number
  activeDialogTitle?: string | null
  topLayer: PreviewInspectObstructionElement[]
  notes: string[]
}

export interface PreviewInspectTargetDiagnostics extends PreviewInspectInteractiveElement {
  selector: string
  found: boolean
  offscreen?: boolean
  obscured?: boolean
  obstructionReason?: string
  interceptedBy?: PreviewInspectObstructionElement | null
  inDialog?: boolean
  dialogTitle?: string | null
}

export interface PreviewInspectPageSnapshot {
  url: string
  title: string
  text: string
  elements: PreviewInspectInteractiveElement[]
  activeElement?: PreviewInspectInteractiveElement | null
  dialogs?: PreviewInspectDialogPresence[]
  obstruction?: PreviewInspectObstructionDiagnostics | null
}

export interface PreviewPerformanceMetrics {
  sampledAt: string
  url: string
  title: string
  navigation?: {
    type?: string
    domContentLoadedMs?: number | null
    loadMs?: number | null
    transferSize?: number | null
    encodedBodySize?: number | null
    decodedBodySize?: number | null
  } | null
  paint?: {
    firstPaintMs?: number | null
    firstContentfulPaintMs?: number | null
  } | null
  webVitals?: {
    lcpMs?: number | null
    cls?: number | null
    inpMs?: number | null
  } | null
  resources?: {
    total?: number
    scripts?: number
    stylesheets?: number
    images?: number
    fonts?: number
    largestTransferSize?: number | null
  } | null
  memory?: {
    usedJsHeapSize?: number | null
    totalJsHeapSize?: number | null
    jsHeapSizeLimit?: number | null
  } | null
}

export interface PreviewInspectRenderState {
  status: 'starting' | 'ready' | 'error' | 'stopped'
  url: string | null
  page: PreviewInspectPageSnapshot | null
  snapshot: string | null
  target?: PreviewInspectTargetDiagnostics | null
  metrics?: PreviewPerformanceMetrics | null
  captureSuppressed?: boolean
  suppressionReason?: string
}

function formatMetricValue(value: number | null | undefined, unit = 'ms'): string {
  if (value == null || !Number.isFinite(value)) return 'n/a'
  if (unit === 'ms') return `${Math.round(value)} ms`
  if (unit === 'bytes') {
    if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`
    if (value >= 1024) return `${(value / 1024).toFixed(1)} KB`
  }
  if (unit === 'count') return `${Math.round(value)}`
  return String(value)
}

export function PreviewMetricsPanel({ metrics }: { metrics?: PreviewPerformanceMetrics | null }) {
  if (!metrics) {
    return <div className="text-muted-foreground">No preview metrics available yet.</div>
  }

  return (
    <div className="space-y-2">
      <div className="rounded border bg-muted/30 p-2">
        <div className="font-medium text-foreground">{metrics.title || '(untitled)'}</div>
        <div className="mt-1 break-all text-muted-foreground">
          <code>{metrics.url}</code>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">Sampled {new Date(metrics.sampledAt).toLocaleTimeString()}</div>
      </div>
      <div className="grid gap-2 md:grid-cols-3">
        <div className="rounded border p-2">
          <div className="font-medium text-foreground">Web Vitals</div>
          <div className="mt-1 space-y-1 text-muted-foreground">
            <div>LCP: {formatMetricValue(metrics.webVitals?.lcpMs, 'ms')}</div>
            <div>CLS: {metrics.webVitals?.cls != null ? metrics.webVitals.cls.toFixed(3) : 'n/a'}</div>
            <div>INP: {formatMetricValue(metrics.webVitals?.inpMs, 'ms')}</div>
          </div>
        </div>
        <div className="rounded border p-2">
          <div className="font-medium text-foreground">Load</div>
          <div className="mt-1 space-y-1 text-muted-foreground">
            <div>DOMContentLoaded: {formatMetricValue(metrics.navigation?.domContentLoadedMs, 'ms')}</div>
            <div>Load: {formatMetricValue(metrics.navigation?.loadMs, 'ms')}</div>
            <div>FCP: {formatMetricValue(metrics.paint?.firstContentfulPaintMs, 'ms')}</div>
          </div>
        </div>
        <div className="rounded border p-2">
          <div className="font-medium text-foreground">Memory</div>
          <div className="mt-1 space-y-1 text-muted-foreground">
            <div>Used JS heap: {formatMetricValue(metrics.memory?.usedJsHeapSize, 'bytes')}</div>
            <div>Total JS heap: {formatMetricValue(metrics.memory?.totalJsHeapSize, 'bytes')}</div>
            <div>Heap limit: {formatMetricValue(metrics.memory?.jsHeapSizeLimit, 'bytes')}</div>
          </div>
        </div>
      </div>
      <div className="rounded border p-2">
        <div className="font-medium text-foreground">Resources</div>
        <div className="mt-1 grid gap-1 text-muted-foreground md:grid-cols-3">
          <div>Total: {formatMetricValue(metrics.resources?.total, 'count')}</div>
          <div>Scripts: {formatMetricValue(metrics.resources?.scripts, 'count')}</div>
          <div>Stylesheets: {formatMetricValue(metrics.resources?.stylesheets, 'count')}</div>
          <div>Images: {formatMetricValue(metrics.resources?.images, 'count')}</div>
          <div>Fonts: {formatMetricValue(metrics.resources?.fonts, 'count')}</div>
          <div>Largest transfer: {formatMetricValue(metrics.resources?.largestTransferSize, 'bytes')}</div>
        </div>
      </div>
    </div>
  )
}

interface ProjectPreviewInspectPanelProps {
  inspectState: PreviewInspectRenderState | null
  loading?: boolean
  error?: string | null
  onInsertElementReference?: (element: PreviewInspectInteractiveElement) => void
}

export function ProjectPreviewInspectPanel({
  inspectState,
  loading = false,
  error = null,
  onInsertElementReference,
}: ProjectPreviewInspectPanelProps) {
  if (error) {
    return (
      <div className="rounded border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-destructive">
        {error}
      </div>
    )
  }

  if (loading) {
    return <div className="text-muted-foreground">Inspecting preview…</div>
  }

  if (inspectState?.captureSuppressed) {
    return (
      <div className="rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1.5 text-amber-700">
        {inspectState.suppressionReason ?? 'Preview inspection is currently suppressed.'}
      </div>
    )
  }

  if (!inspectState) {
    return <div className="text-muted-foreground">No inspection data available yet.</div>
  }

  return (
    <>
      <div className="rounded border bg-muted/30 p-2">
        <div className="font-medium text-foreground">{inspectState.page?.title || '(untitled)'}</div>
        <div className="mt-1 break-all text-muted-foreground">
          <code>{inspectState.url ?? inspectState.page?.url ?? 'Unknown URL'}</code>
        </div>
      </div>
      <div className="rounded border p-2">
        <div className="font-medium text-foreground">Active Element</div>
        {inspectState.page?.activeElement ? (
          <div className="mt-1 space-y-1 text-muted-foreground">
            <div>{[inspectState.page.activeElement.role ?? inspectState.page.activeElement.tagName ?? 'element', inspectState.page.activeElement.name].filter(Boolean).join(' - ')}</div>
            {inspectState.page.activeElement.selector ? <div><code>{inspectState.page.activeElement.selector}</code></div> : null}
            {inspectState.page.activeElement.selector && onInsertElementReference ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 h-7 px-2 text-xs"
                onClick={() => onInsertElementReference(inspectState.page!.activeElement!)}
              >
                Insert Into Chat
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="mt-1 text-muted-foreground">No active input detected.</div>
        )}
      </div>
      <div className="rounded border p-2">
        <div className="font-medium text-foreground">Dialogs</div>
        {inspectState.page?.dialogs?.length ? (
          <div className="mt-1 space-y-1 text-muted-foreground">
            {inspectState.page.dialogs.map((dialog, index) => (
              <div key={`${dialog.selector ?? dialog.title ?? index}`} className="rounded bg-muted/40 px-2 py-1">
                <div>{dialog.title || dialog.name || dialog.role || 'dialog'}</div>
                {dialog.selector ? <div><code>{dialog.selector}</code></div> : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-1 text-muted-foreground">No visible dialogs detected.</div>
        )}
      </div>
      <div className="rounded border p-2">
        <div className="font-medium text-foreground">Obstruction</div>
        {inspectState.page?.obstruction ? (
          <div className="mt-1 space-y-1 text-muted-foreground">
            <div>
              Modal: {inspectState.page.obstruction.hasModal ? 'yes' : 'no'} · Dialogs: {inspectState.page.obstruction.dialogCount}
            </div>
            {inspectState.page.obstruction.activeDialogTitle ? (
              <div>Active dialog: {inspectState.page.obstruction.activeDialogTitle}</div>
            ) : null}
            {inspectState.page.obstruction.notes.map((note, index) => (
              <div key={`${note}:${index}`}>{note}</div>
            ))}
          </div>
        ) : (
          <div className="mt-1 text-muted-foreground">No obstruction diagnostics available.</div>
        )}
      </div>
      {inspectState.target ? (
        <div className="rounded border p-2">
          <div className="font-medium text-foreground">Selector Diagnosis</div>
          <div className="mt-1 space-y-1 text-muted-foreground">
            <div><code>{inspectState.target.selector}</code></div>
            <div>Found: {inspectState.target.found ? 'yes' : 'no'}</div>
            {inspectState.target.obscured != null ? <div>Obscured: {inspectState.target.obscured ? 'yes' : 'no'}</div> : null}
            {inspectState.target.offscreen != null ? <div>Offscreen: {inspectState.target.offscreen ? 'yes' : 'no'}</div> : null}
            {inspectState.target.dialogTitle ? <div>Dialog: {inspectState.target.dialogTitle}</div> : null}
            {inspectState.target.obstructionReason ? <div>{inspectState.target.obstructionReason}</div> : null}
            {inspectState.target.interceptedBy ? (
              <div>
                Intercepted by {inspectState.target.interceptedBy.role ?? inspectState.target.interceptedBy.tagName ?? 'element'}
                {inspectState.target.interceptedBy.selector ? ` (${inspectState.target.interceptedBy.selector})` : ''}
              </div>
            ) : null}
            {inspectState.target.selector && onInsertElementReference ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 h-7 px-2 text-xs"
                onClick={() => onInsertElementReference(inspectState.target!)}
              >
                Insert Into Chat
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="rounded border p-2">
        <div className="font-medium text-foreground">Interactive Elements</div>
        {inspectState.page?.elements?.length ? (
          <div className="mt-2 space-y-2">
            {inspectState.page.elements.map((element, index) => {
              const title = [element.role ?? element.tagName ?? 'element', element.name || element.text].filter(Boolean).join(' - ')
              return (
                <div key={`${element.selector ?? title ?? 'element'}:${index}`} className="rounded bg-muted/40 px-2 py-1.5 text-muted-foreground">
                  <div className="font-medium text-foreground">{title || 'Unnamed element'}</div>
                  {element.selector ? <div className="mt-1 break-all"><code>{element.selector}</code></div> : null}
                  {element.placeholder ? <div className="mt-1">Placeholder: {element.placeholder}</div> : null}
                  {element.value ? <div className="mt-1">Value: {element.value}</div> : null}
                  <div className="mt-1 text-2xs">
                    {element.disabled ? 'Disabled' : 'Enabled'}
                    {element.active ? ' · Active' : ''}
                  </div>
                  {element.selector && onInsertElementReference ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 h-7 px-2 text-xs"
                      onClick={() => onInsertElementReference(element)}
                    >
                      Insert Into Chat
                    </Button>
                  ) : null}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="mt-1 text-muted-foreground">No interactive elements captured.</div>
        )}
      </div>
      <div className="rounded border p-2">
        <div className="font-medium text-foreground">Metrics</div>
        <div className="mt-2">
          <PreviewMetricsPanel metrics={inspectState.metrics} />
        </div>
      </div>
    </>
  )
}
